<template>
  <form class="form" @submit.prevent="onSearch">
    <div class="search-box">
      <input
        class="form-control"
        :class="{ warning: isInvalidInternetNumber }"
        :placeholder="`${$t('searchBox.searchHint')}`"
        type="text"
        v-model.trim="searchQuery"
        ref="searchQueryInput"
      />
      <button
        class="search-clear"
        v-show="searchQuery"
        type="button"
        @click.prevent="clearQuery()"
      >
        <fa icon="times-circle"></fa>
      </button>
      <button
        class="search-submit"
        type="button"
        @click="onSearch"
        :value="$t('searchBox.search')"
      >
        <fa icon="search"></fa>
      </button>
    </div>
    <div v-if="isInvalidInternetNumber" class="invalid-input-warning">
      {{ $t('searchBox.warningText') }}
    </div>
    <label v-if="networkInfo && !isInvalidInternetNumber" class="example">
      <small>
        <i>{{ $t('searchBox.eg', 1) }}:</i>
        <router-link tag="a" :to="'/search/AS' + networkInfo.asn">
          AS{{ networkInfo.asn }}
        </router-link>
        {{ $t('searchBox.or') }}
        <router-link tag="a" :to="'/search/' + networkInfo.prefix">
          {{ networkInfo.prefix }}
        </router-link>
      </small>
    </label>
  </form>
</template>

<script>
import { isNavigationFailure, NavigationFailureType } from 'vue-router';
import {
  INTERNET_NUMBER_RESOURCE_TYPE,
  validateInternetNumberType,
  normalizeInternetNumber,
} from '../utils/validateInternetNumberResource';

export default {
  data() {
    return {
      searchQuery: '',
      isInvalidInternetNumber: false,
    };
  },
  computed: {
    networkInfo() {
      return this.showInfo ? this.$store.state.networkInfo : null;
    },
  },
  props: {
    showInfo: { type: Boolean, default: true },
    activeTab: { type: String, default: 'apnic-at-a-glance' },
    isAutoFocus: { type: Boolean, default: false },
    canGetNetworkInfo: { type: Boolean, default: true },
  },
  watch: {
    searchQuery() {
      if (this.searchQuery === '') this.isInvalidInternetNumber = false;
    },
  },
  mounted() {
    const width = window.innerWidth;
    if (width > 768) this.$store.commit('setShowSideBar', true);

    if (this.isAutoFocus) {
      this.$refs.searchQueryInput.focus();
    }
  },
  methods: {
    clearQuery() {
      this.searchQuery = '';
    },
    onSearch() {
      if (!this.searchQuery) {
        return;
      }

      const validateResult = validateInternetNumberType(this.searchQuery);

      if (validateResult === INTERNET_NUMBER_RESOURCE_TYPE.INVALID) {
        this.isInvalidInternetNumber = true;
        return;
      } else {
        this.isInvalidInternetNumber = false;

        this.searchQuery = normalizeInternetNumber(this.searchQuery);

        // The route of /search/* is transitional, which will redirect to another route.
        // The vue-router will throw an error in this case as the final route is not /search/*.
        // Catch the error here to avoid confusion.
        this.$router
          .push(`/search/${this.searchQuery}#tab=${this.activeTab}`)
          .catch((err) => {
            const isRedirectFailure = isNavigationFailure(
              err,
              NavigationFailureType.redirected
            );

            if (!isRedirectFailure) {
              console.error('Error: ', err);
            }
          });
      }
    },
  },
  created() {
    if (this.canGetNetworkInfo) this.$store.dispatch('getNetworkInfo');
  },
};
</script>

<style scoped lang="scss">
.form {
  width: 100%;

  .example {
    margin-top: 4px;
  }
}

.search-box {
  position: relative;
  max-width: 400px;

  .form-control {
    font-size: 16px;
    padding-right: 30px;
  }

  .warning {
    color: $warning-red;
    border-color: $warning-red;
    box-shadow: none;
  }

  svg[data-icon='search'] {
    color: #007bbd;
    font-size: 18px;
  }

  .search-clear {
    color: #999;
    background: transparent;
    border-color: transparent;
    position: absolute;
    top: 6px;
    right: 50px;
  }
  .text-secondary {
    margin-top: 4px;
  }
  .search-submit {
    color: #999;
    background: transparent;
    border-color: transparent;
    position: absolute;
    top: 5px;
    right: 1px;
  }
}

.invalid-input-warning {
  margin-top: 4px;
  margin-bottom: 8px;
  font-size: 0.75rem;
  color: $warning-red;
}
</style>
